import { useQuery } from '@tanstack/react-query'
import { ApiClientError } from '@/lib/api-client'
import { orgFetch } from '@/lib/org-api'
import { useAuthStore } from '@/lib/auth-store'
import { PageHeader } from '@/components/PageHeader'
import { DataTable } from '@/components/DataTable'
import { AsyncBoundary } from '@/components/AsyncBoundary'

interface OrgSubscription {
  id: string
  organisationId: string
  plan: 'solo' | 'management' | 'enterprise'
  artistSeats: number
  monthlyMinor: number
  currency: string
  discountPercent: number
  stripeSubId: string | null
  currentPeriodEnd: string | null
}

interface FeeSummary {
  totalGrossMinor: number
  totalFeesMinor: number
  count: number
  byType: Record<string, { gross: number; fees: number; count: number }>
}

interface FeeRow {
  id: string
  artistId: string
  type: string
  grossAmountMinor: number
  feeAmountMinor: number
  feePercent: number
  currency: string
  createdAt: string
  showId: string | null
}

export function RosterBillingPage() {
  const user = useAuthStore((s) => s.user)

  const subQuery = useQuery<OrgSubscription | null, ApiClientError>({
    queryKey: ['org-subscription'],
    queryFn: () => orgFetch<OrgSubscription | null>('/billing'),
    enabled: !!user?.organisationId,
  })

  const summaryQuery = useQuery<FeeSummary, ApiClientError>({
    queryKey: ['org-fee-summary'],
    queryFn: () => orgFetch<FeeSummary>('/billing/fees/summary'),
    enabled: !!user?.organisationId,
  })

  const feesQuery = useQuery<FeeRow[], ApiClientError>({
    queryKey: ['org-fees'],
    queryFn: () => orgFetch<FeeRow[]>('/billing/fees'),
    enabled: !!user?.organisationId,
  })

  if (!user?.organisationId) {
    return (
      <div>
        <PageHeader title="Billing" />
        <div className="rounded-md border border-ovation-ink/10 bg-white p-6 text-sm text-ovation-muted">
          Billing is only available to org owners.
        </div>
      </div>
    )
  }

  return (
    <div>
      <PageHeader
        title="Billing"
        subtitle="Subscription, plan, and the transaction fee ledger."
      />

      {/* Subscription card */}
      <AsyncBoundary isLoading={subQuery.isLoading} error={subQuery.error}>
        {subQuery.data ? (
          <div className="mb-6 rounded-lg border border-ovation-ink/10 bg-white p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-xs uppercase tracking-wider text-ovation-muted">
                  Current plan
                </div>
                <div className="mt-1 font-display text-3xl capitalize">
                  {subQuery.data.plan}
                </div>
                <div className="mt-1 text-sm text-ovation-muted">
                  £{(subQuery.data.monthlyMinor / 100).toLocaleString()}/mo
                  {subQuery.data.discountPercent > 0 && (
                    <span className="ml-2 text-emerald-700">
                      ({subQuery.data.discountPercent}% roster discount)
                    </span>
                  )}
                </div>
                <div className="mt-2 text-sm">
                  {subQuery.data.artistSeats} artist
                  {subQuery.data.artistSeats !== 1 ? 's' : ''}
                </div>
              </div>
              <div className="text-right">
                {subQuery.data.stripeSubId ? (
                  <div className="text-xs text-ovation-muted">
                    Stripe ID: <code>{subQuery.data.stripeSubId}</code>
                  </div>
                ) : (
                  <div className="text-xs text-amber-700">
                    No Stripe subscription yet — Phase G
                  </div>
                )}
                {subQuery.data.currentPeriodEnd && (
                  <div className="mt-1 text-xs text-ovation-muted">
                    Renews{' '}
                    {new Date(
                      subQuery.data.currentPeriodEnd,
                    ).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            No subscription record yet. Sign up via the artist registration flow
            to create one.
          </div>
        )}
      </AsyncBoundary>

      {/* Fee summary */}
      {summaryQuery.data && (
        <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SummaryCard
            label="Gross volume YTD"
            value={`£${(summaryQuery.data.totalGrossMinor / 100).toLocaleString()}`}
          />
          <SummaryCard
            label="Platform fees YTD"
            value={`£${(summaryQuery.data.totalFeesMinor / 100).toLocaleString()}`}
          />
          <SummaryCard
            label="Transactions"
            value={summaryQuery.data.count.toString()}
          />
        </div>
      )}

      {/* Ledger */}
      <h2 className="mb-3 font-display text-xl">Transaction fee ledger</h2>
      <AsyncBoundary isLoading={feesQuery.isLoading} error={feesQuery.error}>
        <DataTable
          rows={feesQuery.data ?? []}
          rowKey={(r) => r.id}
          emptyMessage="No transaction fees recorded yet."
          columns={[
            {
              key: 'date',
              header: 'Date',
              render: (r) => new Date(r.createdAt).toLocaleDateString(),
            },
            { key: 'type', header: 'Type', render: (r) => r.type },
            {
              key: 'gross',
              header: 'Gross',
              align: 'right',
              render: (r) => `£${(r.grossAmountMinor / 100).toLocaleString()}`,
            },
            {
              key: 'pct',
              header: 'Fee %',
              align: 'right',
              render: (r) => `${r.feePercent}%`,
            },
            {
              key: 'fee',
              header: 'Fee',
              align: 'right',
              render: (r) => `£${(r.feeAmountMinor / 100).toLocaleString()}`,
            },
          ]}
        />
      </AsyncBoundary>
    </div>
  )
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-ovation-ink/10 bg-white p-5">
      <div className="text-xs uppercase tracking-wider text-ovation-muted">
        {label}
      </div>
      <div className="mt-2 font-display text-3xl">{value}</div>
    </div>
  )
}
